import React, { useState, useEffect } from "react";
import Ratio from "./Ratio";

import "./General.css";

const primaryVoltageOptions = ["250 V", "500 V", "1000 V ", "2500 V", "5000 V"];
const limitedVoltageOptions = ["250 V", "500 V"];
const unitOptions = ["Units", "Ω", "KΩ", "MΩ", "GΩ", "TΩ"];

const CTReport = ({ selectedCore = 3, goBack, savedData = null, onReturnToRatio }) => {
  const RESET_EVENT_NAME = 'ctReportDataCleared';

  const saveToLocalStorage = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
     
    }
  };

  const loadFromLocalStorage = (key) => {
    try {
      const savedData = localStorage.getItem(key);
      return savedData ? JSON.parse(savedData) : null;
    } catch (error) {
     
      return null;
    }
  };

  const clearLocalStorage = (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
     
    }
  };

  const getInitialData = () => {
    const localStorageData = loadFromLocalStorage('ctGeneralData');

    if (localStorageData && localStorageData.selectedCore === selectedCore) {
      return localStorageData;
    }

    return savedData || {
      formData: {},
      rows: [],
      generalInspection: {
        visualInspection: "",
        wiringChecking: "",
        erectionChecking: ""
      },
      units: [],
      selectedCore: selectedCore
    };
  };

  const initialData = getInitialData();

  const [formData, setFormData] = useState(initialData.formData || {});
  const [rows, setRows] = useState(initialData.rows || []);
  const [showNextPage, setShowNextPage] = useState(false);

  const [generalInspection, setGeneralInspection] = useState(
    initialData.generalInspection || {
      visualInspection: "",
      wiringChecking: "",
      erectionChecking: ""
    }
  );
  const [units, setUnits] = useState(initialData.units || []);

  useEffect(() => {
    const dataToSave = {
      formData,
      rows,
      generalInspection,
      units,
      selectedCore,
      timestamp: new Date().toISOString()
    };

    saveToLocalStorage('ctGeneralData', dataToSave);
  }, [formData, rows, generalInspection, units, selectedCore]);


useEffect(() => {
  const generateDynamicReferences = () => {
    const references = ["Primary - Earth"];
    const primaryCoreRefs = [];
    for (let i = 1; i <= selectedCore; i++) {
      primaryCoreRefs.push(`Primary - Core${i}`);
    }
    const coreEarthRefs = [];
    for (let i = 1; i <= selectedCore; i++) {
      coreEarthRefs.push(`Core ${i} - Earth`);
    }
    const coreCoreRefs = [];
    for (let i = 1; i <= selectedCore; i++) {
      for (let j = i + 1; j <= selectedCore; j++) {
        coreCoreRefs.push(`Core${i}-Core${j}`);
      }
    }
    return [
      ...references,
      ...primaryCoreRefs,
      ...coreEarthRefs,
      ...coreCoreRefs
    ];
  };

  const dynamicReferences = generateDynamicReferences();

  const localStorageData = loadFromLocalStorage('ctGeneralData');
  
  
  const shouldReinitialize = !localStorageData ||
    localStorageData.selectedCore !== selectedCore ||
    localStorageData.rows?.length !== dynamicReferences.length;

  if (shouldReinitialize) {
    setRows(dynamicReferences.map(() => ({ r: "", y: "", b: "" })));
    setFormData((prev) => {
      const updatedData = {};
      dynamicReferences.forEach((ref) => {
        updatedData[ref] = "";
      });
      return updatedData;
    });
    setUnits(dynamicReferences.map(() => ({ r: "MΩ", y: "MΩ", b: "MΩ" })));
  }
}, [selectedCore]);







  const handleChange = (index, field, value) => {
    const numericValue = value.replace(/[^0-9. > <]/g, '');
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: numericValue };
    setRows(newRows);
  };

  const handleUnitChange = (index, field, value) => {
    const newUnits = [...units];
    newUnits[index] = { ...newUnits[index], [field]: value };
    setUnits(newUnits);
  };

  const handleVoltageChange = (reference, value) => {
    setFormData((prev) => ({ ...prev, [reference]: value }));
  };

  const handleGeneralInspectionChange = (field, value) => {
    setGeneralInspection(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    const collectedData = {
      formData,
      rows,
      generalInspection,
      units,
      selectedCore
    };

    saveToLocalStorage('ctGeneralData', {
      ...collectedData,
      timestamp: new Date().toISOString()
    });

    if (typeof onReturnToRatio === 'function') {
      onReturnToRatio(collectedData);
      return collectedData;
    }

    setShowNextPage(true);
    return collectedData;
  };

  const handleBackClick = () => {

    const formDataToSave = {
      formData,
      rows,
      generalInspection,
      units,
      selectedCore
    };

    saveToLocalStorage('ctGeneralData', {
      ...formDataToSave,
      timestamp: new Date().toISOString()
    });


    if (goBack) {
      goBack(formDataToSave);
    }
  };

  const handleBackFromRatio = () => {
    setShowNextPage(false);
  };



  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const clearGeneralData = () => {
      clearLocalStorage('ctGeneralData');
      setFormData({});
      setRows([]);
      setGeneralInspection({
        visualInspection: "",
        wiringChecking: "",
        erectionChecking: ""
      });
      setUnits([]);
    };

    const handleGlobalReset = () => {
      clearGeneralData();
      setShowNextPage(false);

    };

    const handleDataReset = () => {
      clearGeneralData();
    };

    window.addEventListener(RESET_EVENT_NAME, handleGlobalReset);
    window.addEventListener('ctReportDataClearedNoRedirect', handleDataReset);
    return () => {
      window.removeEventListener(RESET_EVENT_NAME, handleGlobalReset);
      window.removeEventListener('ctReportDataClearedNoRedirect', handleDataReset);
    };
  }, [selectedCore]);

  const redirectToHome = () => {
    if (window.confirm("Are you sure you want to leave this page.")) {
      localStorage.setItem('openReportsOnLoad', 'true');
      window.location.href = "/";
    }
  };
  const handleExportData = () => {
    const dataToExport = {
      formData,
      rows,
      generalInspection,
      units,
      selectedCore,
      exportTimestamp: new Date().toISOString()
    };

    const dataStr = JSON.stringify(dataToExport, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ct-report-data-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };


  if (showNextPage) {
    return (
      <Ratio
        selectedCore={selectedCore}
        goBackToPolarity={handleBackFromRatio}
        savedGeneralData={{
          formData,
          rows,
          generalInspection,
          units
        }}
      />
    );
  }




  return (
    <div className="GI-00">
      <span
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          fontSize: '20px',
          fontWeight: '600',
          color: '#333',
          cursor: 'pointer',
          marginLeft: '50px',
          marginBottom: '50px'
        }}
        onClick={redirectToHome}
      >
        X
      </span>

      <div style={{ marginTop: "30px" }}>
        <h2>GENERAL INSPECTION</h2> <br />

        <div className="GI-01">
          <div className="GI-02">
            <div className="GI-03">
              <label className="GI-04">
                Visual Inspection
              </label>
              <select
                className="GI-05"
                value={generalInspection.visualInspection}
                onChange={(e) => handleGeneralInspectionChange('visualInspection', e.target.value)}
              >
                <option value="">Select</option>
                <option value="OK">OK</option>
                <option value="Not OK">Not OK</option>
              </select>
            </div>

            <div className="GI-03">
              <label className="GI-04">
                Wiring Checking
              </label>
              <select
                className="GI-05"
                value={generalInspection.wiringChecking}
                onChange={(e) => handleGeneralInspectionChange('wiringChecking', e.target.value)}
              >
                <option value="">Select</option>
                <option value="OK">OK</option>
                <option value="Not OK">Not OK</option>
              </select>
            </div>

            <div className="GI-03">
              <label className="GI-04">
                Erection Checking
              </label>
              <select
                className="GI-05"
                value={generalInspection.erectionChecking}
                onChange={(e) => handleGeneralInspectionChange('erectionChecking', e.target.value)}
              >
                <option value="">Select</option>
                <option value="OK">OK</option>
                <option value="Not OK">Not OK</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="GI-06" style={{ marginTop: "30px" }}>
        <h2 className="place1">INSULATION RESISTANCE MEASUREMENT</h2> <br />
        <table border="1" cellPadding="8" className="GI-08">
          <thead>
            <tr className="GI-07">
              <th>Reference</th>
              <th>Applied Voltage</th>
              <th colSpan="6">     Measured Value</th>
            </tr>
            <tr>
              <th></th>
              <th></th>
              <th>R-ph</th>
              <th>Unit</th>
              <th>Y-ph</th>
              <th>Unit</th>
              <th>B-ph</th>
              <th>Unit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const reference = Object.keys(formData)[index];

              const voltageChoices = reference && reference.startsWith("Primary")
                ? primaryVoltageOptions
                : limitedVoltageOptions;

              return (
                <tr key={index}>
                  <td>{reference}</td>
                  <td>
                    <select
                      value={formData[reference] || ""}
                      onChange={(e) => handleVoltageChange(reference, e.target.value)}
                    >
                      <option value="">Select Voltage</option>
                      {voltageChoices.map((voltage) => (
                        <option key={voltage} value={voltage}>
                          {voltage}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.r || ""}
                      onChange={(e) => handleChange(index, "r", e.target.value)}
                      style={{ width: "60px" }}
                      pattern="[0-9]*\.?[0-9]*"
                      inputMode="decimal"
                    />
                  </td>
                  <td>
                    <select
                      value={units[index]?.r || "MΩ"}
                      onChange={(e) => handleUnitChange(index, "r", e.target.value)}
                    >
                      {unitOptions.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.y || ""}
                      onChange={(e) => handleChange(index, "y", e.target.value)}
                      style={{ width: "60px" }}
                      pattern="[0-9]*\.?[0-9]*"
                      inputMode="decimal"
                    />
                  </td>
                  <td>
                    <select
                      value={units[index]?.y || "MΩ"}
                      onChange={(e) => handleUnitChange(index, "y", e.target.value)}
                    >
                      {unitOptions.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.b || ""}
                      onChange={(e) => handleChange(index, "b", e.target.value)}
                      style={{ width: "60px" }}
                      pattern="[0-9]*\.?[0-9]*"
                      inputMode="decimal"
                    />
                  </td>
                  <td>
                    <select
                      value={units[index]?.b || "MΩ"}
                      onChange={(e) => handleUnitChange(index, "b", e.target.value)}
                    >
                      {unitOptions.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="GI-09">
        <button type="button" className="GI-10" onClick={handleBackClick}>
          Back
        </button>
        <button type="button" className="GI-11" onClick={handleNext}>
          Next
        </button>
      </div>
    </div>
  );
};

export default CTReport;